import React, { forwardRef } from "react";

/**
 * ─── Button ────────────────────────────────────────────────────────────────
 * The ONLY button primitive in the Owner Console (PDR v2.1 §8, item #2).
 *
 * Props
 *   variant     "primary" | "secondary" | "ghost" | "danger" | "danger-solid"
 *   size        "sm" | "md" | "lg"     (default md)
 *   iconLeft    ReactNode
 *   iconRight   ReactNode
 *   iconOnly    boolean   (renders a square icon-only button)
 *   loading     boolean   (shows spinner, disables click, keeps width)
 *   disabled    boolean
 *   as          "button" | "a"           (rare; keep default when possible)
 *   testId      string
 *
 * States handled: default · hover · focus-visible · active · disabled · loading
 * Accessibility: `aria-busy` when loading, `aria-label` required when iconOnly.
 */
const Button = forwardRef(function Button(
  {
    variant = "primary",
    size = "md",
    iconLeft,
    iconRight,
    iconOnly = false,
    loading = false,
    disabled = false,
    className = "",
    children,
    testId,
    type = "button",
    as: Tag = "button",
    ...rest
  },
  ref,
) {
  const classes = [
    "ds-btn",
    `ds-btn--${variant}`,
    `ds-btn--${size}`,
    iconOnly && "ds-btn--icon",
    className,
  ].filter(Boolean).join(" ");

  return (
    <Tag
      ref={ref}
      type={Tag === "button" ? type : undefined}
      className={classes}
      disabled={Tag === "button" ? (disabled || loading) : undefined}
      aria-disabled={disabled || loading || undefined}
      aria-busy={loading || undefined}
      data-testid={testId}
      {...rest}
    >
      {loading
        ? <span className="ds-btn__spinner" aria-hidden />
        : (iconLeft && <span aria-hidden style={{ display: "inline-flex" }}>{iconLeft}</span>)}
      {!iconOnly && <span>{children}</span>}
      {!loading && iconRight && <span aria-hidden style={{ display: "inline-flex" }}>{iconRight}</span>}
    </Tag>
  );
});

export default Button;
